/**
 * Returns an array of changes describing the difference between the lists.
 * The lists may be nested.
 *
 * The original list is an array of { id: <id>, name: <string>, children: [] }
 * objects, the new list is an array of { name, children } objects.
 *
 * A return array contains objects {
 *   what: <'inserted'/'deleted'/'changed'>,
 *   id: <original list item identificator the change is related to>,
 *   name: <an item name>
 *   idx: <item index in the list>
 * }
 */
// eslint-disable-next-line complexity
function NestedListDiff(origItems, newItems, origParentId) {
  let finalDiff = [];

  if (origItems.length == 0 && newItems.length == 0) {
    return finalDiff;
  }

  let origNames = origItems.map(i => i.name);
  let newNames = newItems.map(i => i.name);

  function insertionDiff(parentId, diffBit, newItems) {
    let newItem = newItems[diffBit.idx];
    if (!newItem) {
      console.error(`No child item in ${parentId} parent`);
      console.error(diffBit);
      console.error(newItems);
    }
    let insDiff = {
      parentId: parentId || null,
      id: newItem && newItem.id,
      idx: diffBit.idx,
      name: diffBit.is,
      what: 'inserted',
      children: [],
      origData: newItem,
    };

    // Iterate over inserted subtree.
    function iterateInserted(insDiff, newKids) {
      for (let i = 0; i < newKids.length; i++) {
        let newKid = newKids[i];
        insDiff.children.push({
          id: newKid.id,
          idx: i,
          name: newKid.name,
          children: [],
        });
        if (newKid.children && newKid.children.length) {
          iterateInserted(insDiff.children[i], newKid.children);
        }
      }
    }
    let newKids = newItem && newItem.children;
    if (newKids) {
      iterateInserted(insDiff, newKids);
    }
    return insDiff;
  }

  let diff = ListDiff(origNames, newNames);

  let itemsShift = 0;
  let diff_idx = 0;
  let new_idx = 0;
  let orig_idx = 0;
  let origKid = null;
  while (orig_idx < origItems.length) {
    origKid = origItems[orig_idx];
    if (diff_idx < diff.length) {
      let diffBit = diff[diff_idx];

      // Process all insertions later in a single run.
      if (diffBit.what == 'inserted' && diffBit.idx == new_idx) {
        diff_idx++;
        new_idx++;
        itemsShift++;
        finalDiff.push(insertionDiff(origParentId, diffBit, newItems));
        continue;
      }

      // Process changes or removals.
      if (orig_idx == diffBit.origIdx) {
        diff_idx++;
        finalDiff.push({
          id: origKid.id,
          name: diffBit.is || diffBit.was,
          idx: orig_idx,
          what: diffBit.what,
          origData: origKid,
          newData: newItems[diffBit.idx],
        });

        // Iterate over a deleted subtree.
        if (diffBit.what == 'deleted') {
          orig_idx++;
          itemsShift--;

          // iterate deleted
          let subDiff = [];
          (function collectDeleted(parent) {
            let kids = parent.children || [];
            for (let kid of kids) {
              subDiff.push({
                id: kid.id,
                name: kid.name,
                what: 'deleted',
                origData: kid,
              });
              collectDeleted(kid);
            }
          })(origKid);

          Array.prototype.push.apply(finalDiff, subDiff);
          continue;
        }

        Array.prototype.push.apply(
          finalDiff,
          NestedListDiff(
            origKid.children || [],
            newItems[new_idx].children || [],
            origKid.id
          )
        );
        orig_idx++;
        new_idx++;
        continue;
      }
    }

    // If items were shifted by insertions or deletions, then update the indices
    // of items enclosed after or between shifted elements.
    if (itemsShift != 0) {
      finalDiff.push({
        id: origKid.id,
        name: origKid.name,
        idx: new_idx,
        what: 'shifted',
        origData: origKid,
      });
    }
    Array.prototype.push.apply(
      finalDiff,
      NestedListDiff(
        origKid.children || [],
        newItems[new_idx].children || [],
        origKid.id
      )
    );
    orig_idx++;
    new_idx++;
  }

  // Process insertions.
  for (let i = diff_idx; i < diff.length; i++) {
    let diffBit = diff[i];
    if (diffBit.what == 'inserted') {
      finalDiff.push(insertionDiff(origParentId, diffBit, newItems));
    }
  }

  return finalDiff;
}

/**
 * Returns an array of changes describing the difference between the lists.
 */
function ListDiff(origKids, newKids, compare) {
  return ListDiffDetailed(origKids, newKids, compare).diff;
}

function ListDiffDetailed(origKids, newKids, compare) {
  let pathObj = ListMinDiffPath(origKids, newKids, compare);

  origKids = pathObj.origKids;
  newKids = pathObj.newKids;
  let offset = pathObj.offset;

  // Generate change/insertion/deletion based on the path.
  function diff_between(diff, start_i, end_i, start_j, end_j, delta) {
    let min =
      end_i - start_i < end_j - start_j ? end_i - start_i : end_j - start_j;
    for (let i = start_i; i < start_i + min; i++) {
      diff.push({
        idx: i - start_i + start_j + offset - delta,
        origIdx: i - start_i + start_j + offset,
        was: origKids[i - start_i + start_j],
        is: newKids[i],
        what: 'changed',
      });
    }

    for (let j = start_j + min; j < end_j; j++) {
      diff.push({
        idx: j + offset - delta,
        origIdx: j + offset,
        was: origKids[j],
        is: '',
        what: 'deleted',
      });
      delta++;
    }
    for (let i = start_i + min; i < end_i; i++) {
      diff.push({
        idx: i + offset,
        is: newKids[i],
        was: '',
        what: 'inserted',
      });
    }
    return delta;
  }

  let diff = [];
  let delta = 0,
    prev_i = 0,
    prev_j = 0;
  for (let s of pathObj.path) {
    delta = diff_between(diff, prev_i, s.i, prev_j, s.j, delta);
    prev_i = s.i + s.len;
    prev_j = s.j + s.len;
  }
  diff_between(diff, prev_i, newKids.length, prev_j, origKids.length, delta);

  return {
    diff,
    pathObj,
  };
}

// eslint-disable-next-line complexity
function ListMinDiffPath(origKids, newKids, compare = (a, b) => a == b) {
  // Slice arrays to exclude matching begging and ending items.
  let offset = 0;
  while (
    offset < newKids.length &&
    offset < origKids.length &&
    compare(newKids[offset], origKids[offset])
  ) {
    offset++;
  }
  newKids = newKids.slice(offset);
  origKids = origKids.slice(offset);

  let newKidsIdx = newKids.length - 1,
    oldKidsIdx = origKids.length - 1;
  while (
    newKidsIdx >= 0 &&
    oldKidsIdx >= 0 &&
    compare(newKids[newKidsIdx], origKids[oldKidsIdx])
  ) {
    newKidsIdx--;
    oldKidsIdx--;
  }
  newKids = newKids.slice(0, newKidsIdx + 1);
  origKids = origKids.slice(0, oldKidsIdx + 1);

  // Calculate Сartesian product of matching lengths.
  let subset_lens = [];
  for (let i = 0; i < newKids.length; i++) {
    subset_lens.push(new Array(origKids.length));
    for (let j = 0; j < origKids.length; j++) {
      let k = i,
        l = j;
      for (; k < newKids.length && l < origKids.length; k++, l++) {
        if (newKids[k] != origKids[l]) {
          break;
        }
      }
      subset_lens[i][j] = k - i;
    }
  }

  // Compute paths of matching subsets.
  let paths = [];
  for (let i = 0; i < newKids.length; i++) {
    for (let j = 0; j < origKids.length; j++) {
      let len = subset_lens[i][j];
      if (len != 0) {
        let path = [
          {
            i,
            j,
            len,
          },
        ];
        paths.push(path);

        for (let k = i + len; k < newKids.length; k++) {
          for (let l = j + len; l < origKids.length; l++) {
            let sublen = subset_lens[k][l];
            if (sublen != 0) {
              path.push({
                i: k,
                j: l,
                len: sublen,
              });
              k += sublen;
              l += sublen;
            }
          }
        }
      }
    }
  }

  // Find a path of maximum length.
  let v = 0;
  let maxPath = null;
  for (let p of paths) {
    for (let s of p) {
      v += s.len;
    }
    if (maxPath < v) {
      maxPath = p;
    }
  }

  return {
    offset,
    origKids,
    newKids,
    path: maxPath || [],
    pathLen: v,
    pathMatrix: subset_lens,
    paths,
  };
}

const nested_list_diff = NestedListDiff;
const list_diff = ListDiff;
const list_diff_detailed = ListDiffDetailed;

export { nested_list_diff, list_diff, list_diff_detailed };
